import { useEffect } from "react";

interface ShortcutOptions {
  /** If true, also fires when an input/textarea/select is focused. Default false. */
  allowInInput?: boolean;
}

/**
 * Register a global keydown shortcut.
 *
 * @param key  KeyboardEvent.key value, e.g. "F2", "?", "Escape"
 * @param handler  Callback — receives the KeyboardEvent
 * @param opts  Optional config
 *
 * @example
 * useShortcut("?", () => setOpen(true));
 * useShortcut("F2", focusSearch, { allowInInput: true });
 */
export function useShortcut(
  key: string,
  handler: (e: KeyboardEvent) => void,
  opts: ShortcutOptions = {},
) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== key) return;

      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      const isInput = tag === "input" || tag === "textarea" || tag === "select";
      if (isInput && !opts.allowInInput) return;

      handler(e);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [key, handler, opts.allowInInput]);
}
